import axios from 'axios';
import API from '@/API';
import { ErrorResponse } from '@/types/ErrorResponse';

const API_URL = `${API}/api`;

interface Tokens {
    access: string;
    refresh: string;
}

const authService = {
    async login({ email, password }: { email: string; password: string }): Promise<Tokens> {
        try {
            const response = await axios.post(`${API_URL}/token/`, { email, password });
            return response.data as Tokens;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return Promise.reject({
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred during login.'
                } as ErrorResponse);
            }
            return Promise.reject({
                status: 500,
                message: 'An unexpected error occurred.'
            } as ErrorResponse);
        }
    },

    async logout(refreshToken: string) {
        try {
            const response = await axios.post(`${API_URL}/token/blacklist/`, { refresh: refreshToken });
            return response.status
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return Promise.reject({
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred during logout.'
                } as ErrorResponse);
            }
            return Promise.reject({
                status: 500,
                message: 'An unexpected error occurred.'
            } as ErrorResponse);
        }
    },

    async refresh(refreshToken: string) {
        try {
            const response = await axios.post(`${API_URL}/token/refresh/`, { refresh: refreshToken });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return Promise.reject({
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred during token refresh.'
                } as ErrorResponse);
            }
            return Promise.reject({
                status: 500,
                message: 'An unexpected error occurred.'
            } as ErrorResponse);
        }
    },

    async verifyToken(accessToken: string): Promise<boolean> {
        try {
            const response = await axios.post(`${API_URL}/token/verify/`, { token: accessToken });
            return response.status === 200;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return Promise.reject({
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while verifying the token.'
                } as ErrorResponse);
            }
            return Promise.reject({
                status: 500,
                message: 'An unexpected error occurred.'
            } as ErrorResponse);
        }
    },

    async getUserPermissions(accessToken: string): Promise<string[]> {
        try {
            const response = await axios.get(`${API_URL}/user/permissions/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
            return response.data.permissions;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return Promise.reject({
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while fetching permissions.'
                } as ErrorResponse);
            }
            return Promise.reject({
                status: 500,
                message: 'An unexpected error occurred.'
            } as ErrorResponse);
        }
    }
};

export default authService;
